/**
 * Toast Store — Zustand store for global toast notifications
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast.success('Salvo com sucesso!');
 *   toast.error('Erro ao salvar');
 *   toast.info('Processando...');
 *   toast.xp('+25 XP');
 */

import { create } from 'zustand';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'xp' | 'energy';
  message: string;
  duration: number;
}

interface ToastStore {
  toasts: Toast[];
  addToast: (type: Toast['type'], message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

let toastCounter = 0;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  addToast: (type, message, duration = 4000) => {
    const id = `toast-${++toastCounter}`;
    set((state) => ({
      toasts: [...state.toasts.slice(-4), { id, type, message, duration }],
    }));
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));

// Standalone toast helper (works outside React components — stores, utils, etc.)
export const toast = {
  success: (message: string) => useToastStore.getState().addToast('success', message),
  error: (message: string) => useToastStore.getState().addToast('error', message, 6000),
  info: (message: string) => useToastStore.getState().addToast('info', message),
  xp: (message: string) => useToastStore.getState().addToast('xp', message, 3000),
  energy: (message: string) => useToastStore.getState().addToast('energy', message, 3000),
};

// Convenience hook with shorthand methods (for React components)
export function useToast() {
  const { addToast } = useToastStore();

  return {
    toast: {
      success: (message: string) => addToast('success', message),
      error: (message: string) => addToast('error', message, 6000),
      info: (message: string) => addToast('info', message),
      xp: (message: string) => addToast('xp', message, 3000),
      energy: (message: string) => addToast('energy', message, 3000),
    },
  };
}
